import React, { useState, useEffect } from 'react';
import { CropperModal } from './cropper-modal';
import { PreviewContainer } from './preview-container';
import { Helper } from './helper';

export const Uploader = ({
  aspectRatio,
  aspectRatioLabel,
  src,
  style,
  className,
  name = 'Media',
  onChange,
  onRemove,
  onMediaClick,
  useAltStyle,
  displayPreviewText,
  acceptedFileTypes = 'image/*,video/*',
  fileType,
  showVideoControls = false,
  canDelete = true,
  pendingCrop = false,
  onCropClick
}: {
  aspectRatio: number;
  aspectRatioLabel: string;
  src?: string;
  style?: React.CSSProperties;
  name?: string;
  className?: string;
  onChange?: (value: { blob: Blob; type: string; file: File }) => Promise<void> | void;
  onRemove?: () => void;
  onMediaClick?: (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => void;
  useAltStyle?: boolean;
  displayPreviewText?: string;
  acceptedFileTypes?: string;
  fileType?: 'image' | 'video';
  showVideoControls?: boolean;
  canDelete?: boolean;
  pendingCrop?: boolean;
  onCropClick?: () => void;
}): JSX.Element => {
  const [fileToUpload, setFile] = useState<File | null>(null);
  const [inputDataSrc, setInputDataSrc] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [currentFileType, setCurrentFileType] = useState<'image' | 'video'>(fileType ?? 'image');
  
  useEffect(() => {
    const fetchData = async (src?:string )=>{
      if (src) {
        const isVideoFile = 
          (typeof src === 'string' && 
            (src.includes('video/') || 
             /\.(mp4|webm|ogg|mov)($|\?)/.test(src.toLowerCase()))) ||
          fileType === 'video' || await Helper.isVideoUrl(src);
        setCurrentFileType(isVideoFile ? 'video' : 'image');
      }
    }

    fetchData(src);
    
  }, [src, fileType]);

  const uploadFile = async (blob: Blob): Promise<void> => {
    try {
      setIsUploading(true);

      if (!fileToUpload) {
        throw new Error('No file found.');
      }

      await onChange?.({
        blob,
        // Use the cropped blob's actual encoded type so the S3 Content-Type matches the bytes
        type: blob.type || fileToUpload.type,
        file: fileToUpload
      });
    } finally {
      setIsUploading(false);
    }
  };

  // Bypass cropper if it's a video
  const uploadDirectly = async (file: File, dataSrc: string): Promise<void> => {
    try {
      setIsUploading(true);

      const response = await fetch(dataSrc);
      const blob = await response.blob();
      
      await onChange?.({
        blob,
        type: file.type,
        file
      });
    } finally {
      setIsUploading(false);
    }
  };

  // Handle file selection
  const handleFileUpdate = ({ file, dataSrc, fileType }: { file: File; dataSrc: string; fileType: string }): void => {
    setFile(file);
    
    // Automatically determine what to do based on file type
    if (fileType === 'video') {
      setCurrentFileType('video');
      void uploadDirectly(file, dataSrc);
    } else {
      // For images, go through the cropper
      setCurrentFileType('image');
      setInputDataSrc(dataSrc);
    }
  };

  return (
    <>
      {inputDataSrc && currentFileType === 'image' && (
        <CropperModal
          show={!!inputDataSrc}
          src={inputDataSrc}
          imageName={name}
          aspectRatio={aspectRatio}
          aspectRatioLabel={aspectRatioLabel}
          mimeType={fileToUpload?.type}
          onUpdate={(b): void => {
            void uploadFile(b);
          }}
          onHide={(): void => {
            setInputDataSrc(null);
          }}
        />
      )}
      <PreviewContainer
        className={className}
        name={name}
        style={style}
        aspectRatio={aspectRatio}
        isUploading={isUploading}
        src={src}
        onUpdate={handleFileUpdate}
        onRemove={onRemove}
        onMediaClick={onMediaClick}
        useAltStyle={useAltStyle}
        displayPreviewText={displayPreviewText}
        acceptedFileTypes={acceptedFileTypes}
        fileType={currentFileType}
        showVideoControls={showVideoControls}
        canDelete={canDelete}
        pendingCrop={pendingCrop}
        onCropClick={onCropClick}
      />
    </>
  );
};